import { ActionIcon, Flex, Menu } from '@mantine/core';
import { IconDownload, IconTrash } from '@tabler/icons-react';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import TreeNodeOptions from '@/components/shared/TreeNodeOptions';
import FieldCheckbox from '@/components/shared/form/FieldCheckbox';
import Loader from '@/components/ui/Loader';
import { getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import { canDownloadDocumentByAvailability, type AvailabilityBlockReason } from '@/entities/Tree';
import { useLocalStore } from '@/hooks/useLocalStore';
import { DocumentActionsStore } from '@/stores/local/shared/DocumentActionsStore';
import { useSidebarDocumentsStore } from '@/stores/local/sidebar/SidebarDocumentsStore';
import { useDevices } from '@/utils/devices';

import s from './Actions.module.scss';

type Props = {
  documentId: string;
  searchResultId: string;
  contentAvailable?: boolean;
  availabilityBlockReason?: AvailabilityBlockReason | null;
  withSelection?: boolean;
};

const Actions: React.FC<Props> = ({
  documentId,
  searchResultId,
  contentAvailable = true,
  availabilityBlockReason = null,
  withSelection,
}) => {
  const { isPhone } = useDevices();
  const { id } = useParams();
  const {
    searchDocumentStore: { collection, selectionStore },
    treeStore,
  } = useSidebarDocumentsStore();

  const actionsFileStore = useLocalStore(() => new DocumentActionsStore({ documentId, organizationId: id, treeStore }));

  const handleStopPropagation = React.useCallback((event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleDownload = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      actionsFileStore.downloadDocument();
    },
    [actionsFileStore],
  );

  const handleDelete = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      await actionsFileStore.deleteDocument();

      if (actionsFileStore.deleteMeta.isLoaded) {
        collection.deleteById(searchResultId);
      }
    },
    [actionsFileStore, collection, searchResultId],
  );

  const isLoading = actionsFileStore.downloadMeta.isLoading || actionsFileStore.deleteMeta.isLoading;
  const canDownload = canDownloadDocumentByAvailability(contentAvailable);
  const lockHint = !contentAvailable && !canDownload ? getDocumentLockMessage(availabilityBlockReason) : null;

  const checkboxState = withSelection ? selectionStore.getItemState(documentId) : null;

  const handleToggleSelection = React.useCallback(() => {
    selectionStore.toggle(documentId);
  }, [selectionStore, documentId]);

  if (isPhone) {
    return <Flex className={s.root} align="center" />;
  }

  return (
    <Flex className={s.root} align="center">
      {isLoading ? (
        <Loader type="oval" size="xs" gap="sm" />
      ) : (
        <ActionIcon
          onClick={handleDownload}
          size={16}
          variant="transparent"
          disabled={!canDownload}
          title={!canDownload ? (lockHint ?? undefined) : undefined}
        >
          <IconDownload className={s.root__icon} />
        </ActionIcon>
      )}
      <TreeNodeOptions
        items={
          <>
            <Menu.Item
              disabled={actionsFileStore.deleteMeta.isLoading || !contentAvailable}
              onClick={handleDelete}
              leftSection={<IconTrash size={16} color="red" className={s.root__remove} />}
            >
              Удалить
            </Menu.Item>
          </>
        }
      />
      {withSelection && checkboxState && (
        <FieldCheckbox
          checked={checkboxState.checked}
          indeterminate={checkboxState.indeterminate}
          onToggle={handleToggleSelection}
          onClick={handleStopPropagation}
        />
      )}
    </Flex>
  );
};

export default observer(Actions);
